import React, { useContext, useEffect, useMemo } from 'react';
import { Typography } from 'antd';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import NavigationHeader from '../../Header/NavigationHeader';
import { formatToINR, formatToINRForStocks } from '../../../../Utils/utils';
import { getMaskedAmount } from '../../common/MaskedAmount';
import { PoweredByIgnosis } from '../../common/PoweredByIgnosis';
import { AppContext } from '../../../../App';
import { handleImageError, getStockLogoSrc } from '../../utils/imageUtils';
import { ReactComponent as ProsIcon } from '../../../../Assets/Pros.svg';
import { ReactComponent as CronsIcon } from '../../../../Assets/cons.svg';
import { ReactComponent as PerformanceUpIcon } from '../../../../Assets/PerformanceUp.svg';
import { ReactComponent as PerformanceDownIcon } from '../../../../Assets/PerformanceDown.svg';
import styles from './index.module.css';

dayjs.extend(advancedFormat);

interface Props {
  holdingData?: any;
  onBack?: () => void;
}

const HoldingsScreen: React.FC<Props> = ({ holdingData, onBack }) => {
  const { isAmountVisible, setBackButtonHandler }: any = useContext(AppContext);
  const handleBack = () => {
    if (onBack) onBack();
  };

  useEffect(() => {
    window.onpopstate = () => {
      window.history.pushState(null, '', window.location.href);
    };
    setBackButtonHandler(() => () => handleBack());
  }, [onBack]);

  const currentValue = useMemo(() => {
    if (!holdingData) return 0;
    const price = Number.isNaN(parseFloat(holdingData.bsePrices)) ? 0 : parseFloat(holdingData.bsePrices);
    return price * holdingData.units;
  }, [holdingData]);

  const investedValue = holdingData?.investedValue || 0;
  const gainLoss = investedValue > 0 ? currentValue - investedValue : 0;
  const hasGainLoss = investedValue > 0 && gainLoss !== 0;

  const logoSrc = getStockLogoSrc(holdingData?.logo, holdingData?.isin);

  const formatMarketCap = (value: number) => {
    if (!value || value <= 0) return null;
    return `${(value / 1e7).toFixed(2)} Cr`;
  };

  const hasPerformance = holdingData?.oneMonthPercentage != null ||
    holdingData?.threeMonthPercentage != null ||
    holdingData?.oneYearPercentage != null;

  const renderPercentage = (value: number | null | undefined) => {
    if (value == null) return <Typography.Text>-</Typography.Text>;
    const isNeg = value < 0;
    return (
      <div className={styles.performanceValue}>
        {isNeg ? <PerformanceDownIcon className={styles.downArrow} /> : <PerformanceUpIcon className={styles.upArrow} />}
        <Typography.Text className={isNeg ? styles.negativeValue : styles.positiveValue}>
          {Math.abs(value).toFixed(2)}%
        </Typography.Text>
      </div>
    );
  };

  const pros = holdingData?.prosConsDTOList?.filter((item: any) => item.category === 'PROS') || [];
  const cons = holdingData?.prosConsDTOList?.filter((item: any) => item.category === 'CONS') || [];

  return (
    <div className={styles.container}>
      <NavigationHeader onBack={handleBack} />
      <div className={styles.content}>
        {/* Header Info */}
        <div className={styles.headerInfo}>
          <div className={styles.headerRow}>
            <div className={styles.logoCircle}>
              <img
                src={logoSrc}
                alt={holdingData?.companyName}
                className={styles.logoImg}
                onError={handleImageError}
              />
            </div>
            <div>
              <div className={styles.headerTitle}>
                <Typography.Text className={styles.companyName}>
                  {holdingData?.companyName}
                </Typography.Text>
                <Typography.Text className={styles.stockType}>
                  · {holdingData?.sector || 'Equity'}
                </Typography.Text>
              </div>
              {holdingData?.date && (
                <Typography.Text className={styles.updatedText}>
                  Updated {dayjs(holdingData.date).format("DD MMM 'YY")}
                </Typography.Text>
              )}
            </div>
          </div>
        </div>

        {/* Amount Section */}
        <div className={styles.amountSection}>
          <Typography.Text className={styles.currentValue}>
            {formatToINR(currentValue.toFixed(0), isAmountVisible)}
          </Typography.Text>
          {isAmountVisible && hasGainLoss && (
            <Typography.Text className={`${styles.gainLossText} ${gainLoss >= 0 ? styles.gainPositive : styles.gainNegative}`}>
              {gainLoss >= 0 ? '+' : ''}{formatToINR(gainLoss.toFixed(0), isAmountVisible)} P&L
            </Typography.Text>
          )}
        </div>

        {/* Quick Info Cards */}
        <div className={styles.quickInfoRow}>
          <div className={styles.quickInfoCard}>
            <Typography.Text className={styles.quickInfoLabel}>LTP</Typography.Text>
            <Typography.Text className={styles.quickInfoValue}>
              {formatToINR(
                (holdingData?.bsePrices || 0).toString(),
                isAmountVisible
              )}
            </Typography.Text>
          </div>
          <div className={styles.quickInfoCard}>
            <Typography.Text className={styles.quickInfoLabel}>Quantity</Typography.Text>
            <Typography.Text className={styles.quickInfoValue}>
              {holdingData?.units}
            </Typography.Text>
          </div>
          {holdingData?.marketCap && (
            <div className={styles.quickInfoCard}>
              <Typography.Text className={styles.quickInfoLabel}>Market Cap</Typography.Text>
              <Typography.Text className={styles.quickInfoValue}>
                {holdingData.marketCap.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}
              </Typography.Text>
            </div>
          )}
        </div>

        {/* Stock Details */}
        <div className={styles.detailsSection}>
          <Typography.Text className={styles.sectionTitle}>Stock Details</Typography.Text>

          <div className={styles.detailRow}>
            <Typography.Text className={styles.detailLabel}>Current Value</Typography.Text>
            <Typography.Text className={styles.detailValue}>
              {formatToINR(currentValue.toFixed(0), isAmountVisible)}
            </Typography.Text>
          </div>

          <div className={styles.detailRow}>
            <Typography.Text className={styles.detailLabel}>Quantity</Typography.Text>
            <Typography.Text className={styles.detailValue}>{holdingData?.units}</Typography.Text>
          </div>

          <div className={styles.detailRow}>
            <Typography.Text className={styles.detailLabel}>Last Trade Price</Typography.Text>
            <Typography.Text className={styles.detailValue}>
              {formatToINR(
                (holdingData?.bsePrices || 0).toString(),
                isAmountVisible
              )}
            </Typography.Text>
          </div>

          {holdingData?.marketCapValue != null && holdingData.marketCapValue > 0 && (
            <div className={styles.detailRow}>
              <Typography.Text className={styles.detailLabel}>Market Capitalisation</Typography.Text>
              <Typography.Text className={styles.detailValue}>
                {formatToINR(holdingData.marketCapValue, isAmountVisible)}
              </Typography.Text>
            </div>
          )}

          {holdingData?.lastWeekPL != null && (
            <div className={styles.detailRow}>
              <Typography.Text className={styles.detailLabel}>Weekly P&L</Typography.Text>
              <Typography.Text className={holdingData.lastWeekPL >= 0 ? styles.detailValuePositive : styles.detailValueNegative}>
                {holdingData.lastWeekPL >= 0 ? '+' : ''}₹{formatToINR(holdingData.lastWeekPL.toFixed(2), isAmountVisible)}
              </Typography.Text>
            </div>
          )}

          {holdingData?.peRatio != null && (
            <div className={styles.detailRow}>
              <Typography.Text className={styles.detailLabel}>P/E Ratio</Typography.Text>
              <Typography.Text className={styles.detailValue}>
                {holdingData.peRatio.toFixed(1)}
                {holdingData?.sectorPeRatio != null && (
                  <span style={{ fontSize: 10, color: 'var(--ct-text-muted)', marginLeft: 4 }}>
                    (sector {holdingData.sectorPeRatio.toFixed(1)})
                  </span>
                )}
              </Typography.Text>
            </div>
          )}
        </div>

        {/* Performance */}
        {hasPerformance && (
          <div className={styles.performanceSection}>
            <Typography.Text className={styles.sectionTitle}>Performance</Typography.Text>
            <div className={styles.performanceRow}>
              <div className={styles.performanceItem}>
                <Typography.Text className={styles.performancePeriod}>1 Month</Typography.Text>
                {renderPercentage(holdingData?.oneMonthPercentage)}
              </div>
              <div className={styles.performanceItem}>
                <Typography.Text className={styles.performancePeriod}>3 Months</Typography.Text>
                {renderPercentage(holdingData?.threeMonthPercentage)}
              </div>
              <div className={styles.performanceItem}>
                <Typography.Text className={styles.performancePeriod}>1 Year</Typography.Text>
                {renderPercentage(holdingData?.oneYearPercentage)}
              </div>
            </div>
          </div>
        )}

        {/* Pros */}
        {pros.length > 0 && (
          <div className={styles.prosConsSection}>
            <Typography.Text className={styles.prosConsTitle}>Pros</Typography.Text>
            <div className={styles.prosConsList}>
              {pros.map((item: any, index: number) => (
                <div key={index} className={styles.prosConsItem}>
                  <ProsIcon />
                  <Typography.Text className={styles.prosConsText}>{item.description}</Typography.Text>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cons */}
        {cons.length > 0 && (
          <div className={styles.prosConsSection}>
            <Typography.Text className={styles.prosConsTitle}>Cons</Typography.Text>
            <div className={styles.prosConsList}>
              {cons.map((item: any, index: number) => (
                <div key={index} className={styles.prosConsItem}>
                  <CronsIcon />
                  <Typography.Text className={styles.prosConsText}>{item.description}</Typography.Text>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <PoweredByIgnosis showBottomPadding={true} />
    </div>
  );
};

export default HoldingsScreen;
